import { useGetBranchesQuery } from "@/services/authAPI";
import {
  Box,
  Divider,
  Flex,
  Modal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { get } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

/* eslint-disable import/no-extraneous-dependencies */
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import type { LatLngExpression } from "leaflet";
import L from "leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

interface Props {
  opened: boolean;
  close: () => void;
}

// Fix icon mặc định của Leaflet (nếu không sẽ không thấy marker)
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER: LatLngExpression = [21.0278, 105.8342]; // Hà Nội

const AtmBranch = ({ opened, close }: Props) => {
  const { t } = useTranslation();
  const [branch, setBranch] = useState<any[]>([]);
  const [activeType, setActiveType] = useState<string>("");
  const [selectedBranch, setSelectedBranch] = useState<any | null>(null);
  const [searchText, setSearchText] = useState("");

  const { data: branchData } = useGetBranchesQuery();

  const result = get(branchData, "body.branchInfos", []) as Array<{
    objectType: string;
    objectName: string;
    fullAddress?: string;
    latitude?: number | string;
    longitude?: number | string;
    [k: string]: any;
  }>;

  // Group theo objectType (Chi nhánh / ATM / …)
  const grouped = useMemo(
    () =>
      Object.entries(
        result.reduce<Record<string, any[]>>((acc, item) => {
          const key = item?.objectType || "OTHER";
          if (!acc[key]) acc[key] = [];
          acc[key].push(item);
          return acc;
        }, {})
      ).map(([key, items]) => ({
        name: key,
        items,
      })),
    [result]
  );

  const handleGetBranch = (type: string) => {
    if (!grouped || grouped.length === 0) return;
    const filtered = grouped.find((item) => item.name === type);

    setBranch(filtered?.items || []);
    setActiveType(type);

    // Set branch đầu tiên để center map
    if (filtered?.items?.length) {
      setSelectedBranch(filtered.items[0]);
    }
  };

  // Lần đầu mở: show tất cả & set center theo bản ghi đầu tiên (nếu có toạ độ)
  useEffect(() => {
    if (result && result.length > 0) {
      setBranch(result);
      setSelectedBranch(result[0] || null);
    }
  }, [result]);

  // Lọc theo text search (tên chi nhánh / địa chỉ)
  const filteredBranches = useMemo(() => {
    if (!searchText.trim()) return branch;

    const lower = searchText.toLowerCase();
    return branch.filter(
      (el) =>
        el?.objectName?.toLowerCase().includes(lower) ||
        el?.fullAddress?.toLowerCase().includes(lower)
    );
  }, [branch, searchText]);

  // Tính center map theo chi nhánh đang chọn (nếu có toạ độ)
  const mapCenter: LatLngExpression = useMemo(() => {
    if (
      selectedBranch &&
      selectedBranch.latitude != null &&
      selectedBranch.longitude != null
    ) {
      const lat = Number(selectedBranch.latitude);
      const lng = Number(selectedBranch.longitude);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        return [lat, lng];
      }
    }
    return DEFAULT_CENTER;
  }, [selectedBranch]);

  // Tạo danh sách marker có toạ độ hợp lệ
  const markers = useMemo(
    () =>
      filteredBranches
        .map((el) => {
          if (el.latitude == null || el.longitude == null) return null;
          const lat = Number(el.latitude);
          const lng = Number(el.longitude);
          if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

          return {
            id: `${el.objectType}-${el.objectName}-${lat}-${lng}`,
            position: [lat, lng] as LatLngExpression,
            name: el.objectName,
            address: el.fullAddress,
          };
        })
        .filter(Boolean) as {
        id: string;
        position: LatLngExpression;
        name: string;
        address?: string;
      }[],
    [filteredBranches]
  );

  return (
    <Modal
      opened={opened}
      onClose={close}
      size="70%"
      centered
      withCloseButton={false}
    >
      <Flex gap={40}>
        {/* Cột trái: filter + list chi nhánh */}
        <Stack w="30%" style={{ gap: "10px" }}>
          <TextInput
            placeholder={`${t("SEARCH")}`}
            icon={<IconSearch size="1rem" />}
            radius={8}
            value={searchText}
            onChange={(e) => setSearchText(e.currentTarget.value)}
          />

          <Flex gap={8} wrap="wrap">
            {grouped &&
              grouped.length > 0 &&
              grouped.map((el) => (
                <Box
                  p={8}
                  key={el.name}
                  style={{
                    border:
                      activeType === el.name
                        ? "1px solid #0073c1"
                        : "1px solid #E1E4EA",
                    width: "fit-content",
                    borderRadius: 20,
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                  onClick={() => handleGetBranch(el.name)}
                >
                  {el.name}
                </Box>
              ))}
          </Flex>

          <Box
            sx={{
              background: "#f7f8fb",
              borderRadius: "4px",
              padding: "10px",
            }}
          >
            <ScrollArea h="60vh">
              {filteredBranches &&
                filteredBranches.length > 0 &&
                filteredBranches.map((el, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Stack
                    style={{
                      gap: "8px",
                      marginBottom: "10px",
                      cursor: "pointer",
                    }}
                    // key={idx}
                    onClick={() => setSelectedBranch(el)}
                  >
                    <Box
                      style={{
                        width: "fit-content",
                        background: "#c9e9fb",
                        color: "#0073c1",
                        borderRadius: "4px",
                        fontSize: "12px",
                        padding: "4px",
                        fontWeight: 600,
                      }}
                    >
                      {el?.objectType}
                    </Box>
                    <Text fz={18} fw={600}>
                      {el?.objectName}
                    </Text>
                    <Text>{el?.fullAddress}</Text>
                    <Divider />
                  </Stack>
                ))}
            </ScrollArea>
          </Box>
        </Stack>

        {/* Cột phải: Map */}
        <Box style={{ flex: 1 }}>
          <Box
            sx={{
              borderRadius: 12,
              overflow: "hidden",
              border: "1px solid #E1E4EA",
              height: "80vh",
            }}
          >
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <MapContainer
              center={mapCenter}
              zoom={13}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom
            >
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {markers.map((m) => (
                <Marker key={m.id} position={m.position}>
                  <Popup>
                    <div style={{ fontSize: 14 }}>
                      <strong>{m.name}</strong>
                      {m.address && (
                        <div style={{ marginTop: 4 }}>{m.address}</div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </Box>
        </Box>
      </Flex>
    </Modal>
  );
};

export default AtmBranch;
